import { t } from '@trimble/i18n';
import type { ClassificationScheme } from '@trimble/types';
import { cssHexToRgbaHex, rgbaHexToCssHex } from '@trimble/utilities';
import { Component, createMemo, createSignal, untrack } from 'solid-js';
import { applyColorInputStyle } from '@trimble/ui-components/solidjs';

export interface PointCloudClassificationEditProps {
  /** Scheme being added (`isNew`) or edited. */
  readonly scheme: ClassificationScheme;
  onSave(scheme: ClassificationScheme): void;
  onCancel(): void;
  readonly testId?: string;
}

const LABEL_CLASSES = 'block text-xs text-gray-8';
const FIELD_GROUP_CLASSES = 'mb-3';
const NAME_INPUT_CLASSES =
  'mt-1 block w-full rounded-md border border-gray-2 bg-white px-2 py-1 text-sm text-[var(--modus-wc-color-base-content,#171c1e)] placeholder:text-gray-5 focus:border-[var(--modus-wc-color-primary,#0063a3)] focus:outline-none';
const COLOR_INPUT_CLASSES =
  'border-0 bg-transparent p-0 shadow-none outline-none focus:border-0 focus:outline-none focus:ring-0 focus:shadow-none focus-visible:outline-none focus-visible:ring-0';

export const PointCloudClassificationEdit: Component<
  PointCloudClassificationEditProps
> = (props) => {
  applyColorInputStyle();

  const rootTestId = (): string =>
    props.testId ?? 'point-cloud-classification-edit';

  const [draftName, setDraftName] = createSignal(
    untrack(() => props.scheme.name)
  );
  const [draftRgba, setDraftRgba] = createSignal(
    untrack(() => props.scheme.rgba)
  );
  const [draftVisible, setDraftVisible] = createSignal(
    untrack(() => props.scheme.visible)
  );

  const isSaveDisabled = createMemo(() => draftName().trim().length === 0);

  const titleKey = (): string =>
    props.scheme.isNew === true
      ? 'shared.ui.pointCloudClassificationDetails.edit.addTitle'
      : 'shared.ui.pointCloudClassificationDetails.edit.editTitle';

  const handleSave = (): void => {
    const trimmed = draftName().trim();
    if (trimmed.length === 0) return;
    props.onSave({
      ...props.scheme,
      name: trimmed,
      rgba: draftRgba(),
      visible: draftVisible(),
      isNew: false,
    });
  };

  return (
    <div
      data-testid={rootTestId()}
      class="flex h-full flex-col pb-8"
      aria-labelledby={`${rootTestId()}-title`}
    >
      <div class="flex flex-shrink-0 flex-row items-center justify-between border-b border-gray-1 p-2">
        <h2
          id={`${rootTestId()}-title`}
          class="min-w-0 flex-1 text-lg font-semibold leading-7 text-[var(--modus-wc-color-base-content,#171c1e)]"
        >
          {t(titleKey())}
        </h2>
        <modus-wc-button
          variant="borderless"
          color="tertiary"
          attr:data-testid={`${rootTestId()}-close`}
          on:buttonClick={() => props.onCancel()}
        >
          <modus-wc-icon
            name="close"
            size="sm"
            decorative={false}
            aria-label={t(
              'shared.ui.pointCloudClassificationDetails.edit.cancel'
            )}
          />
        </modus-wc-button>
      </div>

      <div class="min-h-0 flex-1 overflow-y-scroll px-4 py-3">
        <div class="mb-4 text-xs italic" data-testid={`${rootTestId()}-notice`}>
          {t('shared.ui.pointCloudClassificationDetails.edit.notice')}
        </div>

        <div class={FIELD_GROUP_CLASSES}>
          <span class={LABEL_CLASSES}>
            {t('shared.ui.pointCloudClassificationDetails.edit.idLabel')}
          </span>
          <span class="text-sm">{props.scheme.id}</span>
        </div>

        <div class={FIELD_GROUP_CLASSES}>
          <span class={LABEL_CLASSES}>
            {t('shared.ui.pointCloudClassificationDetails.edit.nameLabel')}
          </span>
          <input
            type="text"
            data-testid={`${rootTestId()}-name`}
            class={NAME_INPUT_CLASSES}
            value={draftName()}
            onInput={(e) => setDraftName(e.currentTarget.value)}
          />
        </div>

        <div class={FIELD_GROUP_CLASSES}>
          <span class={LABEL_CLASSES}>
            {t('shared.ui.pointCloudClassificationDetails.edit.colorLabel')}
          </span>
          <div class="mt-[0.15rem] mb-2 h-8">
            <input
              type="color"
              data-testid={`${rootTestId()}-color`}
              class={COLOR_INPUT_CLASSES}
              value={rgbaHexToCssHex(draftRgba())}
              onInput={(e) => {
                setDraftRgba(
                  cssHexToRgbaHex(e.currentTarget.value, draftRgba())
                );
              }}
            />
          </div>
        </div>

        <div class={FIELD_GROUP_CLASSES}>
          <span class={LABEL_CLASSES}>
            {t(
              'shared.ui.pointCloudClassificationDetails.edit.visibilityLabel'
            )}
          </span>
          <div class="mt-1 flex flex-row items-center gap-2">
            <modus-wc-switch
              size="sm"
              attr:value={draftVisible()}
              attr:data-testid={`${rootTestId()}-visible`}
              on:inputChange={(e) => {
                const checked = (
                  (e as CustomEvent).detail.target as
                    | HTMLInputElement
                    | undefined
                )?.checked;
                setDraftVisible(checked === true);
              }}
            />
            <span class="text-xs">
              {t(
                draftVisible()
                  ? 'shared.ui.pointCloudClassificationDetails.edit.visibilityOn'
                  : 'shared.ui.pointCloudClassificationDetails.edit.visibilityOff'
              )}
            </span>
          </div>
        </div>
      </div>

      <div class="grid flex-shrink-0 grid-cols-2 gap-3 border-t border-gray-1 p-4">
        <modus-wc-button
          aria-label={t(
            'shared.ui.pointCloudClassificationDetails.edit.cancel'
          )}
          color="primary"
          size="md"
          variant="outlined"
          attr:full-width={true}
          attr:data-testid={`${rootTestId()}-cancel`}
          on:buttonClick={() => props.onCancel()}
        >
          {t('shared.ui.pointCloudClassificationDetails.edit.cancel')}
        </modus-wc-button>
        <modus-wc-button
          aria-label={t('shared.ui.pointCloudClassificationDetails.edit.save')}
          color="primary"
          size="md"
          variant="filled"
          attr:full-width={true}
          disabled={isSaveDisabled()}
          attr:data-testid={`${rootTestId()}-save`}
          on:buttonClick={handleSave}
        >
          {t('shared.ui.pointCloudClassificationDetails.edit.save')}
        </modus-wc-button>
      </div>
    </div>
  );
};
